import { ancestorsOf } from './taxonomy.js';
const readString = (record, key) => {
    const value = record.fields.get(key)?.v;
    return typeof value === 'string' ? value : '';
};
const readOptions = (record) => {
    const value = record.fields.get('options')?.v;
    return Array.isArray(value)
        ? value.filter((entry) => typeof entry === 'string')
        : [];
};
const TYPES = [
    'text',
    'number',
    'date',
    'single-select',
    'multi-select',
    'boolean',
];
/** Defaults to text for a record whose type is missing or unrecognized. */
const readType = (record) => {
    const value = record.fields.get('type')?.v;
    return TYPES.includes(value) ? value : 'text';
};
export const isSelectType = (type) => type === 'single-select' || type === 'multi-select';
/**
 * The fields that apply to a category: its own, plus everything inherited.
 *
 * Ordered with the most general first, so a field inherited from "Clothing"
 * appears above one defined on "Clothing / Tops". That reads as the broader
 * question being asked before the narrower one, which is the order someone
 * filling the form expects.
 */
export function fieldsForCategory(manifest, categoryId) {
    if (categoryId === null || categoryId === '')
        return [];
    const chain = ancestorsOf(manifest.categories, categoryId);
    if (chain.length === 0)
        return [];
    const defs = [];
    for (const [fieldId, record] of manifest.customFieldDefs) {
        if (record.lifecycle?.present === false)
            continue;
        const owner = readString(record, 'categoryId');
        const distance = chain.indexOf(owner);
        if (distance === -1)
            continue;
        defs.push({
            fieldId,
            categoryId: owner,
            name: readString(record, 'name'),
            type: readType(record),
            options: readOptions(record),
            inheritedFrom: distance + 1,
        });
    }
    return defs.sort((a, b) => b.inheritedFrom - a.inheritedFrom || a.name.localeCompare(b.name));
}
/** Every live definition, for a management view that is not scoped to one item. */
export function allFieldDefs(manifest) {
    const defs = [];
    for (const [fieldId, record] of manifest.customFieldDefs) {
        if (record.lifecycle?.present === false)
            continue;
        defs.push({
            fieldId,
            categoryId: readString(record, 'categoryId'),
            name: readString(record, 'name'),
            type: readType(record),
            options: readOptions(record),
            inheritedFrom: 1,
        });
    }
    return defs.sort((a, b) => a.name.localeCompare(b.name));
}
const sameField = (a, b) => a.name.trim().toLowerCase() === b.name.trim().toLowerCase() && a.type === b.type;
/**
 * The value an item holds for a definition (FR-4.8).
 *
 * Looks under the definition's own id first. Failing that, it looks for a value
 * stored against a *different* definition with the same name and type -- which is
 * how an answer survives the item being refiled into another category.
 *
 * Resolved when read rather than rewritten when the category changes. Rewriting
 * would mean emitting events for a move, which is destructive if the name match
 * turns out to be a coincidence, does not help when the move arrives from another
 * device, and cannot be undone by moving back. Reading is none of those things.
 */
export function valueFor(manifest, item, def) {
    const own = item.customFields.get(def.fieldId);
    if (own !== undefined)
        return own.v;
    const others = allFieldDefs(manifest);
    for (const [fieldId, cell] of item.customFields) {
        if (fieldId === def.fieldId)
            continue;
        const candidate = others.find((other) => other.fieldId === fieldId);
        if (candidate !== undefined && sameField(candidate, def))
            return cell.v;
    }
    return undefined;
}
/**
 * Turns what was typed into what the schema stores, or explains why it cannot.
 *
 * An empty entry is `null` -- "cleared" -- rather than an error, because leaving
 * a custom field blank has to be allowed: FR-2.6 makes everything but the name
 * optional, and that applies to fields someone defined for themselves too.
 */
export function coerceFieldValue(def, raw) {
    if (def.type === 'boolean') {
        return { ok: true, value: raw === true || raw === 'true' };
    }
    if (def.type === 'multi-select') {
        const chosen = Array.isArray(raw) ? raw : raw === '' ? [] : [String(raw)];
        const unknown = chosen.filter((entry) => !def.options.includes(entry));
        if (unknown.length > 0) {
            return { ok: false, error: `“${unknown[0]}” is not one of the choices.` };
        }
        return { ok: true, value: chosen.length === 0 ? null : chosen };
    }
    const text = Array.isArray(raw) ? (raw[0] ?? '') : String(raw);
    if (text.trim() === '')
        return { ok: true, value: null };
    switch (def.type) {
        case 'single-select':
            return def.options.includes(text)
                ? { ok: true, value: text }
                : { ok: false, error: `“${text}” is not one of the choices.` };
        case 'number': {
            const parsed = Number(text);
            // `Number('')` is 0 and `Number('12abc')` is NaN; both would be wrong here,
            // and the blank case has already been handled above.
            if (!Number.isFinite(parsed))
                return { ok: false, error: 'That is not a number.' };
            return { ok: true, value: parsed };
        }
        case 'date':
            // A calendar date with no time and no zone, like every other date in the
            // model (NFR-FUNC-4).
            if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
                return { ok: false, error: 'That is not a date.' };
            }
            return { ok: true, value: text };
        default:
            return { ok: true, value: text };
    }
}
/** Renders a stored value for an input, so an edit round-trips unchanged. */
export function formatFieldValue(value) {
    if (value === null || value === undefined)
        return '';
    if (Array.isArray(value))
        return value.join(', ');
    if (typeof value === 'boolean')
        return value ? 'Yes' : 'No';
    return String(value);
}
//# sourceMappingURL=customfields.js.map